import { createContext, useContext } from "react";

import type { ApiManagerStatus, ApiTaskCheckpoint } from "@/lib/api";

export type LivestreamConnectionState = "online" | "offline" | null;
export type AgentState =
  | "working"
  | "idle"
  | "waking_up"
  | "shutting_down"
  | "offline"
  | null;
export type SocketState =
  | "idle"
  | "offline"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error";

export type TerminalChunk = {
  id: number;
  sequence: number;
  data: Uint8Array;
};

export type TerminalReplayStep =
  | {
      kind: "output";
      data: Uint8Array;
    }
  | {
      kind: "resize";
      cols: number;
      rows: number;
    };

export type TerminalReplay = {
  id: number;
  endSequence: number;
  cols: number;
  rows: number;
  steps: TerminalReplayStep[];
} | null;

export type InstanceSessionState = {
  terminalChunks: TerminalChunk[];
  terminalReplay: TerminalReplay;
  terminalReplayRequestId: number;
  terminalReplayInProgress: boolean;
  terminalReplayEndSequence: number;
  lastTerminalSequence: number;
  managerSubdomain: string | null;
  livestreamState: LivestreamConnectionState;
  agentState: AgentState;
  agentStateMessage: string | null;
  livestreamMessage: string | null;
  workingDirectory: string | null;
  taskId: string | null;
  taskName: string | null;
  taskCheckpoints: ApiTaskCheckpoint[];
};

export type SendMessageInput = {
  instanceId?: string | null;
  taskId?: string | null;
  messageType: "command";
  text: string;
  replyTo?: string | null;
  clientMessageId?: string;
};

export type SendMessageResult = {
  command?: { id?: string | null } | null;
};

export type WakeInstanceInput = {
  instanceId: string;
  taskId?: string | null;
  clientMessageId?: string;
};

export type WakeInstanceResult =
  | {
      status: "accepted";
    }
  | {
      status: "capacity_blocked";
      code: "MAX_CONCURRENCY_REACHED";
      runningCount: number;
      maxConcurrency: number;
    };

export type ShutdownInstanceInput = {
  instanceId: string;
  clientMessageId?: string;
};

export type ShutdownInstanceResult = {
  status: "accepted";
};

export type ManagerConnectionContextValue = {
  managerOnline: boolean;
  managerSubdomain: string | null;
  activeManagerId: string | null;
  activeManagerStatus: ApiManagerStatus | null;
  socketState: SocketState;
  socketStatesBySubdomain: Record<string, SocketState>;
  instanceSessions: Record<string, InstanceSessionState>;
  setPreferredManagerSubdomain: (subdomain: string | null) => void;
  subscribeToInstance: (instanceId: string, subdomain?: string | null) => void;
  unsubscribeFromInstance: (
    instanceId: string,
    subdomain?: string | null,
  ) => void;
  sendMessage: (
    input: SendMessageInput,
    subdomain?: string | null,
  ) => Promise<SendMessageResult>;
  refreshManagerFolders: (subdomain?: string | null) => Promise<void>;
  requestTaskPickup?: (subdomain?: string | null) => Promise<void>;
  wakeInstance: (
    input: WakeInstanceInput,
    subdomain?: string | null,
  ) => Promise<WakeInstanceResult>;
  shutdownInstance: (
    input: ShutdownInstanceInput,
    subdomain?: string | null,
  ) => Promise<ShutdownInstanceResult>;
  sendTerminalInput: (
    instanceId: string,
    data: string,
    subdomain?: string | null,
  ) => void;
  setTerminalSize: (
    instanceId: string,
    cols: number,
    rows: number,
    subdomain?: string | null,
  ) => void;
};

export const ManagerConnectionContext =
  createContext<ManagerConnectionContextValue | null>(null);

export function useManagerConnection() {
  const value = useContext(ManagerConnectionContext);
  if (!value) {
    throw new Error(
      "useManagerConnection must be used within a ManagerConnectionProvider.",
    );
  }
  return value;
}
